import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { metersService, customersService, importExportService } from '../services/api';
import { Plus, Search, Edit, Trash2, Loader, X, Check, Download, Upload, Eye, Filter, Lock, Unlock, Shield, ShieldOff, Zap } from 'lucide-react';
import { format } from "date-fns";
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import Pagination from '../components/Pagination';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';

// ✅ NOUVEAU : souscriptions Victron (doit rester synchro avec
// VICTRON_SUBSCRIPTIONS côté backend : services/victron_payg_service.py)
const VICTRON_SUBSCRIPTIONS = [
  { key: 'S1', label: 'S1 · 75W', price: 4000 },
  { key: 'S2', label: 'S2 · 150W', price: 6000 },
  { key: 'S3', label: 'S3 · 200W', price: 9000 },
];

const Compteurs = () => {
  const { hasPermission } = usePermissions();
  const [meters, setMeters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [editingMeter, setEditingMeter] = useState(null);
  const [alertConfig, setAlertConfig] = useState({isOpen: false, title: '', message: '', type: 'info'});
  const [confirmModal, setConfirmModal] = useState({isOpen: false, title: "", message: "", onConfirm: null, type: "danger"});
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [installationAddresses, setInstallationAddresses] = useState([]);
  const [professionsList, setProfessionsList] = useState([]);
  const itemsPerPage = 20;
  const [filters, setFilters] = useState({
    status: '',
    installation_address: '',
    meter_brand: '',
    profession: ''
  });

  useEffect(() => {
    fetchInstallationAddresses();
    fetchProfessions();
  }, []);

  const fetchInstallationAddresses = async () => {
    try {
      const response = await metersService.getUniqueAddresses();
      setInstallationAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Erreur chargement adresses:', error);
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await metersService.getUniqueProfessions();
      setProfessionsList(response.data.professions || []);
    } catch (error) {
      console.error('Erreur chargement professions:', error);
    }
  };

  const [formData, setFormData] = useState({
    meter_id: '',
    customer_id: '',
    meter_brand: 'STRON',
    meter_type: 'energy',
    serial_number: '',
    phase_type: 'monophase',
    departement: '',
    commune: '',
    profession: '',
    installation_address: '',
    sts_key: '',
    tariff: 0,
    monthly_fee: 0,
    max_power: 0,
    installation_date: '',
    notes: '',
    // ✅ NOUVEAU : config Victron intégrée directement dans le formulaire
    victron_serial: '',
    victron_key: '',
    victron_subscription: 'S1',
    victron_starting_code: '',   // ✅ AJOUT
    concentrator_id: '',         // ✅ ID collecteur (eau) / concentrateur (élec)
  });

  useEffect(() => {
    if (showModal) {
      searchCustomers(customerSearch);
    } else {
      setCustomers([]);
      setCustomerSearch('');
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    const timer = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchMeters();
  }, [searchTerm, filters, currentPage]);

  const fetchMeters = async () => {
    setLoading(true);
    try {
      const metersRes = await metersService.getAll({
        search: searchTerm,
        status: filters.status,
        installation_address: filters.installation_address,
        meter_brand: filters.meter_brand,
        profession: filters.profession,
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      });

      setMeters(metersRes.data.items || metersRes.data);
      setTotalItems(metersRes.data.total || metersRes.data.length);
    } catch (error) {
      console.error('Erreur chargement:', error);
      showAlert("Erreur", "Erreur lors du chargement des compteurs", "error");
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = async (query = '') => {
    setCustomerSearchLoading(true);
    try {
      const response = await customersService.search(query, 50);
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Erreur recherche clients:', error);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ isOpen: true, title, message, type });
  };

  const isVictronBrand = formData.meter_brand === 'VICTRON';
  const isWater = formData.meter_type === 'water'; // ✅ compteur à eau (toutes marques)
  // Marques dont la VENTE d'eau est intégrée (doit correspondre à WATER_PROVIDERS côté backend)
  const WATER_SALE_BRANDS = ['GOMELONG'];
  const waterSaleAvailable = isWater && WATER_SALE_BRANDS.includes((formData.meter_brand || '').toUpperCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.meter_id) errors.meter_id = "Le numéro du compteur est obligatoire";
    if (!formData.meter_brand) errors.meter_brand = "La marque est obligatoire";
    if (!formData.installation_address) errors.installation_address = "L'adresse d'installation est obligatoire";
    // ✅ Tarif obligatoire seulement si PAS Victron
    if (!isVictronBrand && (!formData.tariff || formData.tariff <= 0)) {
      errors.tariff = "Le tarif doit être supérieur à 0";
    }
  
    // ✅ NOUVEAU : validation config Victron si marque = VICTRON
     if (isVictronBrand) {
      if (!formData.victron_serial?.trim()) errors.victron_serial = "Le numéro de série Victron est obligatoire";
      if (!formData.victron_key?.trim()) errors.victron_key = "La clé privée OpenPAYGO est obligatoire";
      if (!formData.victron_starting_code) errors.victron_starting_code = "Le trading code (code de démarrage) est obligatoire";  // ✅ AJOUT
      if (!formData.victron_subscription) errors.victron_subscription = "La souscription (S1/S2/S3) est obligatoire";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setActionLoading(true);
    try {
      // Ne pas envoyer les champs victron_* si la marque n'est pas VICTRON
     const payload = { ...formData };
      if (!payload.concentrator_id) payload.concentrator_id = null;
      if (!isVictronBrand) {
        payload.victron_serial = null;
        payload.victron_key = null;
        payload.victron_subscription = null;
        payload.victron_starting_code = null;
      } else if (payload.victron_starting_code) {
        payload.victron_starting_code = parseInt(payload.victron_starting_code);
      }
      if (editingMeter) {
        await metersService.update(editingMeter.meter_id, payload);
      } else {
        await metersService.create(payload);
      }
      setShowModal(false);
      resetForm();
      fetchMeters();
      fetchProfessions();
      fetchInstallationAddresses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Erreur serveur";
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail
          .map((e) => `${e.loc?.[e.loc.length - 1] || "champ"}: ${e.msg}`)
          .join(" | ");
      }
      setFormErrors({ submit: errorMessage });
    }finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (meter) => {
    setEditingMeter(meter);

    let customerIdValue = '';
    if (meter.customer) {
      customerIdValue = meter.customer.customer_id;
      if (!customers.find(c => c.customer_id === customerIdValue)) {
        setCustomers([{
          id: meter.customer.id,
          customer_id: meter.customer.customer_id,
          full_name: meter.customer.full_name,
          phone: meter.customer.phone
        }]);
      }
    }

    setFormData({
      meter_id: meter.meter_id,
      customer_id: customerIdValue,
      meter_brand: meter.meter_brand || 'STRON',
      meter_type: meter.meter_type,
      serial_number: meter.serial_number || '',
      phase_type: meter.phase_type || 'monophase',
      departement: meter.departement || '',
      commune: meter.commune || '',
      profession: meter.profession || '',
      installation_address: meter.installation_address || '',
      sts_key: meter.sts_key || '',
      tariff: meter.tariff || 0,
      monthly_fee: meter.monthly_fee || 0,
      max_power: meter.max_power || 0,
      installation_date: meter.installation_date || '',
      notes: meter.notes || '',
      // ✅ NOUVEAU
      victron_serial: meter.victron_serial || '',
      victron_key: meter.victron_key || '',
      victron_subscription: meter.victron_subscription || 'S1',
      victron_starting_code: meter.victron_starting_code || '',   // ✅ AJOUT
      concentrator_id: meter.concentrator_id || '',               // ✅ collecteur

    });

    setCustomerSearch('');
    setShowModal(true);
  };

  const askDeleteConfirmation = (meter) => {
    setConfirmModal({
      isOpen: true,
      title: "⚠️ Supprimer le compteur",
      message: `Êtes-vous sûr de vouloir supprimer définitivement le compteur ${meter.meter_id} ?`,
      type: "danger",
      onConfirm: () => confirmDelete(meter)
    });
  };

  const confirmDelete = async (meter) => {
    try {
      setActionLoading(true);
      await metersService.delete(meter.meter_id);
      showAlert("✅ Succès", `Le compteur ${meter.meter_id} a été supprimé`, "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", error.response?.data?.detail || "Erreur suppression", "error");
    } finally {
      setActionLoading(false);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const askBlockConfirmation = (meter) => {
    setConfirmModal({
      isOpen: true,
      title: "🔒 Bloquer le compteur",
      message: `Voulez-vous bloquer le compteur ${meter.meter_id} ?`,
      type: "danger",
      onConfirm: () => confirmBlockMeter(meter)
    });
  };

  const confirmBlockMeter = async (meter) => {
    try {
      setActionLoading(true);
      await metersService.block(meter.meter_id);
      showAlert("✅ Succès", `Le compteur ${meter.meter_id} a été bloqué`, "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", error.response?.data?.detail || "Erreur blocage", "error");
    } finally {
      setActionLoading(false);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const askUnblockConfirmation = (meter) => {
    setConfirmModal({
      isOpen: true,
      title: "🔓 Débloquer le compteur",
      message: `Voulez-vous débloquer le compteur ${meter.meter_id} ?`,
      type: "warning",
      onConfirm: () => confirmUnblockMeter(meter)
    });
  };

  const confirmUnblockMeter = async (meter) => {
    try {
      setActionLoading(true);
      await metersService.unblock(meter.meter_id);
      showAlert("✅ Succès", `Le compteur ${meter.meter_id} a été débloqué`, "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", error.response?.data?.detail || "Erreur déblocage", "error");
    } finally {
      setActionLoading(false);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const askManualBlockConfirmation = (meter) => {
    setConfirmModal({
      isOpen: true,
      title: "🔒 Bloquer manuellement",
      message: `Voulez-vous bloquer manuellement le compteur ${meter.meter_id} ?\n\n⚠️ Ce blocage ne sera pas levé automatiquement, même après paiement des redevances.`,
      type: "danger",
      onConfirm: () => confirmManualBlock(meter)
    });
  };

  const confirmManualBlock = async (meter) => {
    try {
      setActionLoading(true);
      await metersService.manualBlock(meter.meter_id);
      showAlert("✅ Succès", `Le compteur ${meter.meter_id} a été bloqué manuellement`, "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", error.response?.data?.detail || "Erreur blocage", "error");
    } finally {
      setActionLoading(false);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const askManualUnblockConfirmation = (meter) => {
    setConfirmModal({
      isOpen: true,
      title: "🔓 Débloquer manuellement",
      message: `Voulez-vous débloquer le compteur ${meter.meter_id} ?`,
      type: "warning",
      onConfirm: () => confirmManualUnblock(meter)
    });
  };

  const confirmManualUnblock = async (meter) => {
    try {
      setActionLoading(true);
      await metersService.manualUnblock(meter.meter_id);
      showAlert("✅ Succès", `Le compteur ${meter.meter_id} a été débloqué`, "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", error.response?.data?.detail || "Erreur déblocage", "error");
    } finally {
      setActionLoading(false);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const handleViewDetails = (meter) => {
    setSelectedMeter(meter);
    setShowDetailsModal(true);
  };

  const handleExport = async () => {
    try {
      const response = await importExportService.exportMeters();
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compteurs_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showAlert("❌ Erreur", "Erreur lors de l'export", "error");
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const result = await importExportService.importMeters(file);
      const message = `✅ ${result.data.created} compteurs importés!${result.data.errors.length > 0 ? '\n⚠️ Erreurs:\n' + result.data.errors.join('\n') : ''}`;
      showAlert("✅ Import terminé", message, result.data.errors.length > 0 ? "warning" : "success");
      fetchMeters();
    } catch (error) {
      showAlert("❌ Erreur", "Erreur lors de l'import", "error");
    }
    event.target.value = '';
  };

  const resetForm = () => {
    setEditingMeter(null);
    setCustomerSearch('');
    setCustomers([]);
    setFormData({
      meter_id: '',
      customer_id: '',
      meter_brand: 'STRON',
      meter_type: 'energy',
      serial_number: '',
      phase_type: 'monophase',
      departement: '',
      commune: '',
      profession: '',
      installation_address: '',
      sts_key: '',
      tariff: 0,
      monthly_fee: 0,
      max_power: 0,
      installation_date: '',
      notes: '',
      // ✅ NOUVEAU
      victron_serial: '',
      victron_key: '',
      victron_subscription: 'S1',
      victron_starting_code: '',   
      concentrator_id: '',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-emerald-100 text-emerald-700', text: 'Actif' },
      inactive: { color: 'bg-slate-100 text-slate-700', text: 'Inactif' },
      suspended: { color: 'bg-orange-100 text-orange-700', text: 'Suspendu' },
      blocked: { color: 'bg-red-100 text-red-700', text: 'Bloqué' }
    };
    const badge = badges[status] || badges.active;
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  const getTypeBadge = (type) => {
    const badges = {
      energy: { color: 'bg-blue-100 text-blue-700', text: 'Électricité' },
      water: { color: 'bg-cyan-100 text-cyan-700', text: 'Eau' },
      gas: { color: 'bg-orange-100 text-orange-700', text: 'Gaz' }
    };
    const badge = badges[type] || badges.energy;
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        loading={actionLoading}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
      />
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gestion des Compteurs</h1>
            <p className="text-slate-600 mt-1">Liste de tous les compteurs du système</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
              <Download size={20} />
              Exporter (Excel)
            </button>
            <label className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 cursor-pointer">
              <Upload size={20} />
              Importer
              <input type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" />
            </label>
            <PermissionGuard permission={PERMISSIONS.ADD_METER}>
              <button onClick={() => { resetForm(); setShowModal(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm">
                <Plus size={20} />
                Nouveau Compteur
              </button>
            </PermissionGuard>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Filtres</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="N° compteur, client, téléphone, adresse..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50"
                />
              </div>
            </div>
            <div>
              <select
                value={filters.installation_address}
                onChange={(e) => setFilters({ ...filters, installation_address: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50"
              >
                <option value="">Toutes les adresses</option>
                {installationAddresses.map((address) => (
                  <option key={address} value={address}>{address}</option>
                ))}
              </select>
            </div>
            <div>
              {/* ✅ CORRIGÉ : VICTRON ajouté au filtre (manquait) */}
              <select value={filters.meter_brand} onChange={(e) => setFilters({ ...filters, meter_brand: e.target.value })}
                className="w-full px-4 py-2.5 border rounded-xl bg-slate-50"
              >
                <option value="">Toutes les marques</option>
                <option value="STRON">STRON</option>
                <option value="GOMELONG">GOMELONG</option>
                <option value="HEXING">HEXING</option>
                <option value="VICTRON">VICTRON</option>
              </select>
            </div>
            <div>
              <select value={filters.profession} onChange={(e) => setFilters({ ...filters, profession: e.target.value })}
                className="w-full px-4 py-2.5 border rounded-xl bg-slate-50"
              >
                <option value="">Toutes les Secteurs</option>
                {professionsList.map((p, index) => (
                  <option key={index} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50">
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="suspended">Bloqué (suspendu + manuel)</option>
              </select>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="w-10 h-10 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">N° Compteur</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Type</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Client</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Adresse D'installation</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Commune</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Secteur</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Date d'installation</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Cumul des achats</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Cumul (kWh / m³)</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Statut</th>
                      <th className="text-left py-4 px-6 text-sm font-bold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {meters.map((meter) => (
                      <tr key={meter.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="py-4 px-6">
                          <span className="font-mono text-sm font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-lg">
                            {meter.meter_id}
                          </span>
                          {/* ✅ NOUVEAU : badge Victron + souscription visible directement dans la liste */}
                          {meter.meter_brand === 'VICTRON' && meter.victron_subscription && (
                            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                              <Zap size={12} /> {meter.victron_subscription}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">{getTypeBadge(meter.meter_type)}</td>
                        <td className="py-4 px-6">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-900">{meter.customer?.full_name || '-'}</span>
                            <span className="text-xs text-slate-500">{meter.customer?.customer_id || ''}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-slate-700 font-medium">{meter.installation_address || '-'}</span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-slate-700 font-medium">{meter.commune || '-'}</span>
                        </td>
                        <td className="py-4 px-6">
                          {meter.profession ? (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                              {meter.profession}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-slate-700 font-medium">
                            {meter.installation_date ? format(new Date(meter.installation_date), "dd/MM/yyyy") : '-'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          {meter.last_purchase && meter.last_purchase.total_count > 0 ? (
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-900">
                                {Number(meter.last_purchase.total_amount).toLocaleString()} F CFA
                              </span>
                              <span className="text-xs text-slate-500">
                                {meter.last_purchase.total_count} achat(s)
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <span className="font-bold text-emerald-600 text-lg">
                            {meter.last_purchase ? Number(meter.last_purchase.total_kwh).toLocaleString() : 0} {meter.meter_type === 'water' ? 'm³' : 'kWh'}
                          </span>
                        </td>
                        <td className="py-4 px-6">{getStatusBadge(meter.status)}</td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleViewDetails(meter)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors" title="Voir les détails">
                              <Eye size={18} />
                            </button>

                            {meter.status === 'suspended' && (
                              <button onClick={() => askUnblockConfirmation(meter)}
                                className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg"
                                title="Suspendu (redevances)">
                                <Unlock size={18} />
                              </button>
                            )}
                            {meter.status === 'blocked' && (
                              <button onClick={() => askManualUnblockConfirmation(meter)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                title="Débloquer (blocage manuel)">
                                <ShieldOff size={18} />
                              </button>
                            )}
                            {meter.status === 'active' && (
                              <PermissionGuard permission={PERMISSIONS.BLOCK_METER}>
                                <button onClick={() => askManualBlockConfirmation(meter)}
                                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                  title="Bloquer manuellement">
                                  <Shield size={18} />
                                </button>
                              </PermissionGuard>
                            )}
                            <PermissionGuard permission={PERMISSIONS.EDIT_METER}>
                              <button onClick={() => handleEdit(meter)} className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors" title="Modifier">
                                <Edit size={18} />
                              </button>
                            </PermissionGuard>
                            <PermissionGuard permission={PERMISSIONS.DELETE_METER}>
                              <button onClick={() => askDeleteConfirmation(meter)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors" title="Supprimer">
                                <Trash2 size={18} />
                              </button>
                            </PermissionGuard>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalItems / itemsPerPage)}
                onPageChange={setCurrentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
              />
            </>
          )}
        </div>
        {showDetailsModal && selectedMeter && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50">
                <h2 className="text-2xl font-bold text-slate-900">Détails du Compteur</h2>
                <button onClick={() => setShowDetailsModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Numéro Compteur</p>
                    <p className="font-mono font-bold text-lg text-slate-900">{selectedMeter.meter_id}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Statut</p>
                    {getStatusBadge(selectedMeter.status)}
                  </div>
                </div>
                {selectedMeter.customer && (
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Informations Client</p>
                    <p className="text-lg font-bold text-slate-900">{selectedMeter.customer.full_name}</p>
                    <p className="text-sm text-slate-600">ID: {selectedMeter.customer.customer_id}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Type</p>
                    {getTypeBadge(selectedMeter.meter_type)}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Marque</p>
                    <p className="font-semibold text-slate-900">{selectedMeter.meter_brand}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Département</p>
                    <p className="font-semibold text-slate-900">{selectedMeter.departement || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Commune</p>
                    <p className="font-semibold text-slate-900">{selectedMeter.commune || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Secteur</p>
                    <p className="font-semibold text-slate-900">{selectedMeter.profession || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Solde Actuel</p>
                    <p className="font-bold text-emerald-600 text-xl">{selectedMeter.current_balance} {selectedMeter.meter_type === 'water' ? 'm³' : 'kWh'}</p>
                  </div>
                </div>
                {/* ✅ NOUVEAU : bloc infos Victron dans les détails */}
                {selectedMeter.meter_brand === 'VICTRON' && (
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1">
                      <Zap size={16} /> Configuration Victron PAYGO
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Serial</p>
                        <p className="font-mono font-semibold">{selectedMeter.victron_serial || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Souscription</p>
                        <p className="font-semibold">
                          {VICTRON_SUBSCRIPTIONS.find(s => s.key === selectedMeter.victron_subscription)?.label || selectedMeter.victron_subscription || '-'}
                        </p>
                      </div>
                    </div>
                    {selectedMeter.victron_expiry_date && (
                      <p className="text-xs text-slate-600 mt-2">
                        Expire le : <span className="font-semibold">{new Date(selectedMeter.victron_expiry_date).toLocaleDateString('fr-FR')}</span>
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500 mb-1">Date d'installation</p>
                  <p className="font-bold text-emerald-600 text-xl">
                    {selectedMeter.installation_date ? new Date(selectedMeter.installation_date).toLocaleDateString('fr-FR') : '-'}
                  </p>
                </div>
                {selectedMeter.last_purchase && selectedMeter.last_purchase.total_count > 0 && (
                  <div className="bg-emerald-50 p-4 rounded-xl">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Cumul des achats</p>
                    <p className="text-2xl font-bold text-emerald-700">
                      {Number(selectedMeter.last_purchase.total_amount).toLocaleString()} XOF
                    </p>
                    <p className="text-sm text-slate-600">
                      {Number(selectedMeter.last_purchase.total_kwh).toLocaleString()} {selectedMeter.meter_type === 'water' ? 'm³' : 'kWh'} cumulés
                    </p>
                    <p className="text-sm text-slate-600">
                      {selectedMeter.last_purchase.total_count} achat(s) au total
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingMeter ? 'Modifier le Compteur' : 'Nouveau Compteur'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">N° Compteur *</label>
                    <input type="text" value={formData.meter_id} onChange={(e) => setFormData({ ...formData, meter_id: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      disabled={!!editingMeter}
                    />
                    {formErrors.meter_id && <p className="text-red-600 text-sm mt-1">{formErrors.meter_id}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Type *</label>
                    <select
                      value={formData.meter_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        meter_type: e.target.value,
                        // ✅ Eau : toutes marques sauf VICTRON (kit solaire)
                        meter_brand: e.target.value === 'water' && formData.meter_brand === 'VICTRON'
                          ? ''
                          : formData.meter_brand,
                      })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      required>
                      <option value="energy">Électricité</option>
                      <option value="water">Eau</option>
                      <option value="gas">Gaz</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Marque *</label>
                    {/* ✅ CORRIGÉ : VICTRON ajouté (manquait, empêchait de créer un compteur Victron) */}
                    <select
                      value={formData.meter_brand}
                      onChange={(e) => setFormData({ ...formData, meter_brand: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                      required
                    >
                      <option value="">-- Choisir une marque --</option>
                      <option value="STRON">STRON</option>
                      <option value="GOMELONG">GOMELONG</option>
                      <option value="HEXING">HEXING</option>
                      {!isWater && <option value="VICTRON">VICTRON (PAYGO / abonnement)</option>}
                    </select>
                    {formErrors.meter_brand && <p className="text-red-600 text-sm mt-1">{formErrors.meter_brand}</p>}
                  </div>
                </div>

                {/* ✅ NOUVEAU BLOC : Configuration Victron — apparaît uniquement si marque = VICTRON */}
                {isVictronBrand && (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 space-y-4">
                    <p className="font-semibold text-amber-800 flex items-center gap-2">
                      <Zap size={18} /> Configuration Victron PAYGO
                    </p>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Numéro de série (SN SHS) *
                      </label>
                      <input
                        type="text"
                        value={formData.victron_serial}
                        onChange={(e) => setFormData({ ...formData, victron_serial: e.target.value })}
                        placeholder="Ex: HQ2219JMZAG"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-mono focus:ring-2 focus:ring-amber-500"
                      />
                      {formErrors.victron_serial && <p className="text-red-600 text-sm mt-1">{formErrors.victron_serial}</p>}
                    </div>
                     {/* ✅ NOUVEAU CHAMP */}
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        Trading code (code de démarrage) *
      </label>
      <input
        type="number"
        value={formData.victron_starting_code}
        onChange={(e) => setFormData({ ...formData, victron_starting_code: e.target.value ? parseInt(e.target.value) : '' })}
        placeholder="Ex: 990649676"
        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-mono focus:ring-2 focus:ring-amber-500"
      />
      {formErrors.victron_starting_code && <p className="text-red-600 text-sm mt-1">{formErrors.victron_starting_code}</p>}
      
    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Clé privée OpenPAYGO (32 hex) *
                      </label>
                      <input
                        type="text"
                        value={formData.victron_key}
                        onChange={(e) => setFormData({ ...formData, victron_key: e.target.value })}
                        placeholder="Ex: b02af346799720d4659cdba87a55f97d"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-mono text-sm focus:ring-2 focus:ring-amber-500"
                      />
                      {formErrors.victron_key && <p className="text-red-600 text-sm mt-1">{formErrors.victron_key}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Souscription *</label>
                      <div className="grid grid-cols-3 gap-2">
                        {VICTRON_SUBSCRIPTIONS.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => setFormData({ ...formData, victron_subscription: s.key })}
                            className={`p-3 rounded-lg border-2 text-center transition-colors ${
                              formData.victron_subscription === s.key
                                ? 'border-amber-600 bg-amber-100'
                                : 'border-slate-200 bg-white hover:border-amber-300'
                            }`}
                          >
                            <p className="font-bold text-sm">{s.label}</p>
                            <p className="text-xs text-slate-500">{s.price.toLocaleString()} F/mois</p>
                          </button>
                        ))}
                      </div>
                      {formErrors.victron_subscription && <p className="text-red-600 text-sm mt-1">{formErrors.victron_subscription}</p>}
                    </div>
                  </div>
                )}

                {/* ✅ BLOC : compteur à eau (toutes marques) */}
                {isWater && (
                  <div className="bg-cyan-50 border-2 border-cyan-200 rounded-xl p-4 space-y-3">
                    <p className="font-semibold text-cyan-800">
                      💧 Compteur à eau {formData.meter_brand || ''}
                    </p>
                    <p className="text-xs text-cyan-700">
                      Vente en m³
                    </p>
                    {formData.meter_brand && !waterSaleAvailable && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
                        ⚠️ La vente d'eau n'est pas encore intégrée pour la marque <b>{formData.meter_brand}</b>.
                      </div>
                    )}
                    {formData.meter_brand === 'GOMELONG' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">ID Collecteur</label>
                      <input
                        type="text"
                        value={formData.concentrator_id}
                        onChange={(e) => setFormData({ ...formData, concentrator_id: e.target.value })}
                        placeholder="Ex : 2408000123"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-mono focus:ring-2 focus:ring-cyan-500"
                      />
                      <p className="text-xs text-slate-500 mt-1">Nécessaire pour le contrôle à distance (vanne, envoi de token, relevés).</p>
                    </div>
                    )}
                  </div>
                )}

                {/* ✅ Concentrateur pour les compteurs électriques GOMELONG communicants */}
                {!isWater && formData.meter_brand === 'GOMELONG' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">ID Concentrateur</label>
                    <input
                      type="text"
                      value={formData.concentrator_id}
                      onChange={(e) => setFormData({ ...formData, concentrator_id: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl font-mono focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Client</label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      placeholder="Rechercher un client..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                    {customerSearchLoading && (
                      <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-blue-600" />
                    )}
                    {customerSearch && !customerSearchLoading && (
                      <button
                        onClick={() => setCustomerSearch('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Aucun client</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.customer_id}>
                        {c.full_name} — {c.customer_id} {c.phone ? `— ${c.phone}` : ''}
                      </option>
                    ))}
                  </select>
                  {customers.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{customers.length} client(s) trouvé(s)</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Département</label>
                    <input
                      type="text"
                      value={formData.departement}
                      onChange={(e) => setFormData({ ...formData, departement: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Dakar"
                    />
                    {formErrors.departement && <p className="text-red-600 text-sm mt-1">{formErrors.departement}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Commune</label>
                    <input
                      type="text"
                      value={formData.commune}
                      onChange={(e) => setFormData({ ...formData, commune: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Guédiawaye"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Secteur</label>
                    <input
                      type="text"
                      value={formData.profession}
                      onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Ménage, Aser, Moulin..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phase</label>
                    <select
                      value={formData.phase_type}
                      onChange={(e) => setFormData({ ...formData, phase_type: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500">
                      <option value="monophase">Monophasé</option>
                      <option value="triphasé">Triphasé</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Adresse d'installation *</label>
                  <textarea
                    value={formData.installation_address}
                    onChange={(e) => setFormData({ ...formData, installation_address: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                  />
                  {formErrors.installation_address && <p className="text-red-600 text-sm mt-1">{formErrors.installation_address}</p>}
                </div>
                {/* Clé STS n'a de sens que pour STRON/GOMELONG/HEXING, pas Victron */}
                {!isVictronBrand && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Clé STS (SGC)</label>
                    <input
                      type="text"
                      value={formData.sts_key}
                      onChange={(e) => setFormData({ ...formData, sts_key: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {isWater ? 'Prix du m³ (XOF/m³) *' : 'Tarif (XOF/kWh) *'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.tariff}
                      onChange={(e) => setFormData({ ...formData, tariff: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    {formErrors.tariff && <p className="text-red-600 text-sm mt-1">{formErrors.tariff}</p>}
                    {isVictronBrand && (
                      <p className="text-xs text-slate-500 mt-1">
                        Non utilisé pour Victron (vente par abonnement mensuel, pas au kWh).
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Redevance mensuelle (XOF)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.monthly_fee}
                      onChange={(e) => setFormData({ ...formData, monthly_fee: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Puissance max (W)</label>
                    <input
                      type="number"
                      value={formData.max_power}
                      onChange={(e) => setFormData({ ...formData, max_power: parseInt(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Date d'installation</label>
                    <input
                      type="date"
                      value={formData.installation_date}
                      onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                  />
                </div>
                {formErrors.submit && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                    {formErrors.submit}
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-colors">
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={actionLoading}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold shadow-lg shadow-blue-500/30">
                    {actionLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Check size={20} />}
                    {editingMeter ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Compteurs;